import { Fragment, memo, useMemo, useRef } from 'react';
import { ImageType, SceneType } from '@types';
import { CheckboxNoFunc } from '@components/Checkbox';
import Image from '@components/Image';
import TextLink from '@components/TextLink';
import cn from '@utils/cn';
import { changeThumb } from '@modules/category/actions/changeThumb';
import RenameSceneModal from '@modules/category/SceneList/RenameSceneModal';
import { renameScene } from '@modules/category/actions/renameScene';
import addComponent from '@utils/addComponent';
import duplicateScene from '@modules/category/actions/duplicateScene';
import replaceScene from '@modules/category/actions/replaceScene';
import ImgPano from '../MSceneList/img-pano.png';
import ImgPVideo from '../MSceneList/img-pvideo.png';
import ImgMatrix from '../MSceneList/img-matrix.png';
import type { SMResultItem } from '../stores';

interface Props {
  data: SMResultItem;
  isDragging: boolean;
  isSelected: boolean;
  isThisDragging: boolean;
  onActive: (item: SMResultItem) => void;
  onDelete: (item: SMResultItem) => void;
  onClick: (item: SMResultItem, isSelected: boolean) => void;
}

export const ResultItem = memo(function ResultItem({
  data,
  isDragging,
  isThisDragging,
  isSelected,
  onActive,
  onDelete,
  onClick,
}: Props) {
  const { id, categories } = data;
  const categoryId = categories[categories.length - 1].id;
  const ref = useRef<HTMLDivElement>(null);

  const markSrc = useMemo(() => {
    if (data.type === SceneType.pano) {
      return ImgPano;
    }
    if (data.type === SceneType.panoVideo) {
      return ImgPVideo;
    }
    if (data.type === SceneType.matrix) {
      return ImgMatrix;
    }
    return null;
  }, [data.type]);

  const handleRename = async () => {
    addComponent((remove) => (
      <RenameSceneModal
        isOpen
        oldName={data.name}
        onRequestClose={remove}
        onConfirm={(result) => {
          renameScene({
            sceneId: id,
            name: result.name,
            renameHotspot: result.renameHotspot,
            renameSandTable: result.renameSandTable,
          });
          remove();
        }}
      />
    ));
  };

  return (
    <div
      ref={ref}
      className={cn(
        'border-t-translate relative flex h-50 justify-between gap-100 border-y p-20',
        {
          'hover:bg-[#f8f8f8]': !isDragging,
          'opacity-50': isThisDragging,
        }
      )}
      onPointerDown={(e) => {
        e.stopPropagation();
        onActive(data);
      }}
      onClick={(e) => {
        e.stopPropagation();
        onClick(data, !isSelected);
      }}
    >
      <div className="flex min-w-0 items-center gap-10">
        <CheckboxNoFunc checked={isSelected} />
        <Image
          className="h-32 w-32 shrink-0"
          src={data.thumbUrl}
          type={ImageType.thumb}
          size={32}
        />
        <div className="flex min-w-0 flex-col">
          <div className=" flex items-center gap-10">
            <div className="truncate" title={data.name}>
              {data.name}
            </div>
            {markSrc && (
              <img src={markSrc} className="h-[16px] w-[26px]" alt="" />
            )}
          </div>
          <div className="flex items-center text-sm text-black-gray">
            {data.categories.map((x, i) => (
              <Fragment key={x.id}>
                {i !== 0 && <div className="mx-5">{'>'}</div>}
                <div className="max-w-[150px] truncate" title={x.name}>
                  {x.name}
                </div>
              </Fragment>
            ))}
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-20 text-sm">
        <TextLink
          onClick={async (e) => {
            e.stopPropagation();
            await changeThumb(data);
          }}
        >
          修改封面
        </TextLink>
        <TextLink
          onClick={(e) => {
            e.stopPropagation();
            handleRename();
          }}
        >
          重命名
        </TextLink>
        <TextLink
          onClick={(e) => {
            e.stopPropagation();
            duplicateScene({ sceneId: id, categoryId });
          }}
        >
          复制
        </TextLink>
        <TextLink
          onClick={async (e) => {
            e.stopPropagation();
            await replaceScene({ sceneType: data.type, id });
          }}
        >
          替换
        </TextLink>
        <TextLink
          onClick={async (e) => {
            e.stopPropagation();
            onDelete(data);
          }}
        >
          删除
        </TextLink>
      </div>
    </div>
  );
});

export default ResultItem;
